import { useCallback, useEffect, useState } from "react"

import api, { getEnterpriseId } from "../../../lib/api"
import { emitSnackbar } from "../../../lib/snackbar-emitter"
import { loader } from "../../../lib/loader"
import manageErrors from "../../../lib/error-management"
import { isValidEmail } from "../../../utils"

const ENTERPRISE_PATH = (enterpriseId) => `enterprises/${enterpriseId}/`

const DEFAULT_SETTINGS = {
    send_scout_email: false,
    scout_email: "",
    scout_export_type: "pdf",
}

export const useReportsSettings = () => {
    const enterpriseId = getEnterpriseId()

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [settings, setSettings] = useState(DEFAULT_SETTINGS)

    const load = useCallback(async () => {
        if (!enterpriseId) return

        setLoading(true)
        loader.on()

        try {
            const response = await api.get(ENTERPRISE_PATH(enterpriseId))

            if (response.status === 200) {
                const apiSettings = response?.data?.settings || {}

                setSettings({
                    send_scout_email: Boolean(apiSettings?.send_scout_email),
                    scout_email: apiSettings?.scout_email || "",
                    scout_export_type:
                        apiSettings?.scout_export_type === "csv" ? "csv" : "pdf",
                })
                return
            }

            emitSnackbar({
                message: manageErrors(response.data),
                severity: "error",
            })
        } catch (error) {
            emitSnackbar({
                message: "Erro ao carregar as configurações de relatórios.",
                severity: "error",
            })
        } finally {
            loader.off()
            setLoading(false)
        }
    }, [enterpriseId])

    useEffect(() => {
        void load()
    }, [load])

    const save = useCallback(async () => {
        if (!enterpriseId) return false

        const normalizedEmail = String(settings.scout_email || "").trim()

        if (settings.send_scout_email && !normalizedEmail) {
            emitSnackbar({
                message: "Informe o e-mail para envio dos relatórios.",
                severity: "error",
            })
            return false
        }

        if (settings.send_scout_email && !isValidEmail(normalizedEmail)) {
            emitSnackbar({
                message: "Informe um e-mail válido para envio dos relatórios.",
                severity: "error",
            })
            return false
        }

        setSaving(true)
        loader.on()

        try {
            const response = await api.patch(ENTERPRISE_PATH(enterpriseId), {
                settings: {
                    send_scout_email: Boolean(settings.send_scout_email),
                    scout_email: String(settings.scout_email || "").trim(),
                    scout_export_type: settings.scout_export_type === "csv" ? "csv" : "pdf",
                },
            })

            if (response.status === 200) {
                emitSnackbar({
                    message: "Configurações de relatórios salvas com sucesso.",
                    severity: "success",
                })
                return true
            }

            emitSnackbar({
                message: manageErrors(response.data),
                severity: "error",
            })
            return false
        } catch (error) {
            const serverError =
                error?.response?.data ||
                (error?.message ? String(error.message) : "")

            emitSnackbar({
                message: `Erro ao salvar as configurações de relatórios.${serverError ? ` (${serverError})` : ""}`,
                severity: "error",
            })
            return false
        } finally {
            loader.off()
            setSaving(false)
        }
    }, [enterpriseId, settings])

    return {
        loading,
        saving,
        settings,
        setSettings,
        load,
        save,
    }
}